import {
  BuildMaterial,
  ChecklistType,
  MainVesselType,
  WorkshopBoatStatus,
  WorkshopUserRole,
  WorkshopWorkOrderSection,
} from '../enums'
import { CheckListCompletion, LocalityFromHavbruksloggen } from '../Http/DataTypes'
import { MainCategory } from '../Http/DataTypes'
import { AnalysisFormDataResponse, WorkshopCardContainerItem } from './workorder-types'

export enum ApprovalStatus {
  NotApproved,
  Approved,
}
export interface IEntity {
  id: number
}
export interface ITimestampEntity extends IEntity {
  updatedAt: string
  createdAt: string
}

export interface IClientUserLink {
  note: string
  validUntil: Date
}
export interface IRiskAnalysisTask extends IEntity {
  clientId: number

  name: string
  metrics?: IRiskAnalysisTaskMetrics
  subTasks?: IRiskAnalysisSubTask[]
}

export interface IRiskAnalysisSubTask extends IEntity {
  taskId: number
  name: string

  task?: IRiskAnalysisTask
}

export interface IRiskAnalysisTaskMetrics extends IEntity {
  taskId: number
  vesselType?: string
  boatLength?: number
  yearBuilt?: number
}

export interface IBoatRiskAnalysisTask extends IEntity {
  riskAnalysisTask: IRiskAnalysisTask
  riskAnalysisTaskId: number
  riskLevelAfterMeasures: number
  riskLevel: number
  name?: string
}

export interface IBoatRiskAnalysisSubTask extends IEntity {
  riskAnalysisSubTask: IRiskAnalysisSubTask
  riskAnalysisSubTaskId: number
  name?: string
  riskLevel: number
  riskLevelAfterMeasures: number
  dangers?: ISubTaskDanger[]
}

export interface ISubTaskDanger {
  id: number
  name: string
  probability: number
  consequence: number
  probabilityAfterMeasure: number
  consequenceAfterMeasure: number
  measures?: ISubTaskMeasure[]
}

export interface ISubTaskMeasure {
  id: number
  description: string
  measureTaken: boolean
  deadlineDate: string
  responsible?: any
  responsibleId: number
  responsibleString: string
}

export interface IBoat {
  id?: number
  boatId: number
  name: string
  buildNo: string
  callsign: string
  imo: string
  loa: number
  loh: number
  mainImageId: number
  mainImage: any
  mmsi: string
  deckLoad: number
  regDate: string
  registeredByUser: IUser
  urlFriendlyName: string
  vesselType: MainVesselType
  yearBuilt: number
  clientId: number
  sensorConfig: string
  owner: string
  supplier?: any
  boatStatusId?: number
  encryptedPermissionValue: string
  permissionValue: number
  isBoatInWorkshop: boolean
  workshopName: string
  workOrders: IWorkshopBoatWorkOrder
  remoteUrl: string
  featureValue: string
  encryptedFeatureValue: string
  stockId: number
  registrationNumber?: string
  assetType?: number
  addressLine1?: string
}

export interface IAsset {
  assetId: number
  type: number
  name: string
  mainImageId: number
  mainImage: any
  registeredByUser: IUser
  urlFriendlyName: string
  clientId: number
  encryptedPermissionValue: string
  permissionValue: number
  isBoatInWorkshop: boolean
  workshopName: string
  workOrders: IWorkshopBoatWorkOrder
  remoteUrl: string
  featureValue: string
  encryptedFeatureValue: string
  stockId: number
  assetStatusId?: number
  boat?: IBoatAsset
  vehicle?: IVehicleAsset
  property?: IPropertyAsset
  fixedAsset?: IFixedAsset
  additionalInfo?: string
  images?: any[]
  accessGroupId?: number
}

export interface IFixedAsset {
  registrationNumber: string
  serialNumber: string
  brand: string
  model: string
  year: number
  mileage: number
  bodyType: string
  odometer: string
  typeOfMachine: string
  enginePower: number
  electricRange: number
  weightClass: string
  feeCategory: string
  color: string
  fuel: string
  capacity: string
  maximumWeight: number
  guarantee: string
  owner: string
  unit: string
}

export interface IPropertyAsset {
  addressLine1: string
  addressLine2: string
  addressLine3: string
  propertyType: string
  owner: string
  size: string
  year: number
  content: string
  description: string
}

export interface IVehicleAsset {
  registrationNumber: string
  chassisNumber: string
  firstTimeRegistered: Date
  brand: string
  model: string
  year: number
  mileage: number
  bodyType: string
  electricRange: number
  feeCategory: string
  color: string
  fuel: string
  wheelDrive: string
  maximumTrailerWeight: number
  guarantee: string
  owner: string
  leasePeriod: string
  usageCategory: string
  tireStatus: string
}

export interface IBoatAsset {
  buildNo: string
  callsign: string
  imo: string
  loa: number
  loh: number
  mmsi: string
  deckLoad: number
  regDate: string
  registeredByUser: IUser
  urlFriendlyName: string
  vesselType: MainVesselType
  yearBuilt: number
  sensorConfig: string
  owner: string
  supplier?: any
  encryptedPermissionValue: string
  permissionValue: number
}

export interface IBoatRiskAnalysisGeneralInfo {
  lastUpdated?: string
  measuresOverDue: number
  measuresWithDueDate: number
}

export interface IUser {
  userId: number
  userStatusId: number
  email: string
  firstName: string
  lastName: string
  cellPhone: string
  userStatus: any
  active: boolean
  fullname: string
  registeredDate: string
  tokenId: string
  registrationToken: string
}

export type ColorUser =
  | {
      fullname: string
      userId: number
      [key: string]: any
    }
  | {
      firstName: string
      lastName: string
      userId: number
      [key: string]: any
    }

export type ColorName =
  | {
      fullname: string
      [key: string]: any
    }
  | {
      firstName: string
      lastName: string
      [key: string]: any
    }

export interface IUserExtended extends IUser {
  role: {
    systemAdmin: boolean
    clientBoatRoles: {
      boatsWithAccess: number[]
      clientAdmin: boolean
      readonly: boolean
      clientId: number
    }[]
    individualBoatsAccess: number[]
    workshopAccess: any[]
  }
  permissionValue: number | null
  userRole: string
  encryptedPermissionValue: string
}

export interface IWorkshopService {
  serviceId: number
  service: IService
}
export interface IService {
  serviceId: number
  equipmentId?: number
  tagId?: number
  title: string
  notes: string
  registeredDate: string
}
export interface IWorkshopUser {
  id: number
  user: IUser
  userId: number
  role: WorkshopUserRole
  workCaseCount: number
}

export interface IStockUser {
  id: number
  name: string
  roleId: number
  roleCategory: number
  roleType: number
}

export interface IGetWorkshopReponse {
  result: IGetWorkshopResponseContent
}

export interface IGetWorkshopResponseContent {
  loggedInWorkshopUserId?: number
  workshop: IWorkshop
  users: IWorkshopUser[]
  permissionValue: number
  encryptedPermissionValue: string
  clientFeatureValue: string
  encryptedClientFeatureValue: string
}

export interface IWorkshop {
  id: number
  clientId: number
  name: string
  organizationNumber: string
  address: string
  postNumber: number
  city: string
  phoneNumber?: string
  description?: string
  clientName?: string
}

export interface ITokenUser {
  fullname: string
  role: {
    ClientBoatRoles: {
      BoatWithAccess: number[]
      ClientAdmin: boolean
      ClientId: number
    }[]
    IndividualBoatCcess: number[]
    SystemAdmin: boolean
  }
  token: string
  type: number
}

export interface IWorkshopBoatStatusResponse {
  isAtWorkshop: boolean
  workshopName: string
}

export interface IWorkshopBoatAttachment {
  id: EntityId
  workshopBoatId: EntityId
  fileId: EntityId

  file: IFile
}

export interface IWorkshopBoat {
  workOrder: any
  workShopUserDetailsDto: any
  endDate: string
  startDate: string
  id: number
  workshopId: number
  boatId: number
  boat: IBoat
  description?: string
  start?: any
  finish?: any
  status: WorkshopBoatStatus
  attachments: IWorkshopBoatAttachment[]
  notes: IWorkshopBoatNote[]
  workshopCaseLogs?: any
  createdAt: string
  updatedAt: string
  connectedServices?: IWorkshopService[]
  workshopBoatCheckLists?: IWorkshopCheckList[]
  users?: any
  workOrders: IWorkOrderParent
  WorkshopCaseLogs: IWorkshopCaseLogs[]
  checklist?: IWorkshopCheckList
  suggestion?: string
  budget: number
  timePlan: number
  workCaseAnalysis?: AnalysisFormDataResponse
  hasOpenDeviation: boolean
}

export interface IWorkshopCaseLogs {
  id: number
  workshopBoatId: number
  workshopCaseLogType: WorkshopCaseLogType
  statusChangedById: number
  statusChangedBy: IWorkshopUser
  workshopBoatStatus: WorkshopBoatStatus
  connectedUsers: string
}

export enum WorkshopCaseLogType {
  Initial = 0,
  StatusChange = 1,
}

export interface IWorkOrderParent {
  boat: any
  id: number
  boatId?: number
  assignedPersonId?: number
  clientId?: number
  workshopId?: number
  dueDate: any
  type: string
  title: string
  description: string
  executionDate: string
  assignedPersonString?: string
  price: number
  autoCalculatePrice: boolean
  completed: boolean
  whenRegistered: string
  items?: any
  files?: any[]
  budget?: number
  timePlan?: number
}

export interface IWorkOrder extends IWorkOrderParent {
  assignedPerson?: string
  workshopBoat?: IWorkshopBoat
  workshopCase?: IWorkshopCase
}

export interface IWorkOrderPreview {
  id: number
  title: string
  description: string
  executionDate: string
  boat: IBoatPreview
  registeredByUser: string
  whenRegistered: string
}

export interface IBoatPreview {
  id: number
  name: string
  mmsi: string
  mainImageRemoteUrl: string
  localities?: LocalityFromHavbruksloggen[]
}

export interface IWorkshopCase {
  id?: number
  workOrderId?: number
  workOrder?: IWorkOrderParent
  users?: Array<IWorkshopCaseUser>
  status?: IWorkshopStatus
  description?: string
  start?: any
  finish?: any
  workshopCaseLogs?: any
}

export enum IWorkshopStatus {
  Pending = 'Pending',
  Accepted = 'Accepted',
  InProgress = 'InProgress',
  ForApproval = 'ForApproval',
  Completed = 'Completed',
  Planned = 'Planned',
}

export interface IBaseWorkshopOverview {
  client: IClient
  workshops: IWorkshopOverview[]
}

export interface IWorkshopOverview {
  workshop: IWorkshop
  totalBoats: number
  totalWorkOrders: number
  notDoneWorkOrders: number
  todaysWorkOrders: number
  overdueWorkOrders: number
  clientId: number
  totalWorkCases: number
  todayWorkCases: number
  sentForApprovalWorkCases?: number
  incomingWorkorders?: number
}

export interface IWorkshopBoatResponse {
  id: number
  status: WorkshopBoatStatus
  boat: IBoat
  connectedServices: number
  workshopCases: number
  workshopBoatCheckLists: number
  createdAt: Date
  acceptedDate: Date
  workShopUserDetailsDto: IWorkshopUser
  users?: IWorkshopUser[]
  description: string
}

export interface IWorkshopCaseUser {
  hoursSpent: number
  user: IWorkshopUser
}

export interface IWorkshopUser {
  id: number
  user: IUser
  userId: number
  role: WorkshopUserRole
  workCaseCount: number
  connectedServices?: any
  timeManagements?: any
  userRole?: any
  userRoleId?: number
  workshopRole?: string
}

export interface IFile {
  fileId: number
  fileContentId: number | null
  folderId: number | null
  boatId: number | null
  boatLogBookId: number | null
  filename: string
  fileType: number
  viewToken: string
  remoteUrl: string
  azureSyncDate: string | null
  contentType: string
  contentLength: number
  registredBy: number
  registredDate: string
  deleted: boolean
  deletedBy: number | null
  whenDeleted: string | null
  mime: string | null
}

export interface IWorkshopBoatNote {
  id: number
  content: string
}

export interface IClient {
  clientId: number
  clientNr?: string
  organizationNr?: string
  name: string
  streetAddress?: string
  city?: string
  state?: string
  zipCode?: string
  telephone?: string
  registeredDate?: string
  permissionValue?: number
  isPartner?: boolean
  featureValue?: string
  encryptedFeatureValue?: string
  featureMappingId?: number
}

export interface IClientMinimal {
  clientId: number
  name: string
  isPartner?: boolean
}

export interface IAccessGroupBoat {
  accessGroupId: number
  accessGroupTitle: string
  boats: IBoatOverviewDto[]
}
export interface ICustomerBoatOverview {
  boats: IBoatOverviewDto[]
  client: IClient
}

export interface IBoatOverviewDto {
  boatId: IBoat['boatId']
  boatTypeId: number | null
  boatTypeCategoryId: number | null
  boatTradeAreaId: number | null
  boatStatusId: IBoat['boatStatusId']
  supplierId: number | null
  documentTemplateId: number | null
  clientId: IBoat['clientId']
  clientName: string | null
  boatHullTypeId: number | null
  mainImageId: IBoat['mainImageId']
  imo: IBoat['imo']
  mmsi: IBoat['mmsi']
  name: IBoat['name']
  urlFriendlyName: IBoat['urlFriendlyName']
  model: string
  callsign: IBoat['callsign']
  vesselType: MainVesselType
  lOA: IBoat['loa']
  lOH: IBoat['loh']
  beam: number | null
  draft: number | null
  displacement: number | null
  displacementMax: number | null
  maximumNumberOfPeopleOnBoard: number | null
  yearBuilt: IBoat['yearBuilt']
  buildNo: IBoat['buildNo']
  certifiedUntil: string | null
  regDate: IBoat['regDate']
  registeredBy: number | null
  registeredByUser: IBoat['registeredByUser']
  numberOfDocumentsCloseToExpire: number | null
  closestTimeForDocumentExpire: string | null
  numberOfDeviations: number | null
  highestDeviationSeverity: number | null
  numberOfMaintancesToday: number | null
  documentTemplateNameInUse: string
  buildMaterial: BuildMaterial
  ensilageCapacity: number | null
  deadWeight: number | null
  owner: IBoat['owner']
  numberOfAttachmentPoints: number | null
  hs: number | null
  vc: number | null
  numberOfFeedingLines: number | null
  totalFeedingCapacity: number | null
  boatType: string
  boatHullType: string
  boatTypeCategory: string
  boatTradeArea: string
  boatStatus: string
  supplier: IBoat['supplier']
  mainImageViewToken: string | null
  mainImageRemoteUrl: string | null
  mainImageName: string
  documentsForDashboard: any
  equipmentsForDashboard: any
  servicesForDashboard: any
  isUserFamiliarizedOnBoat: boolean
  numberOfDocumentsWithDueDate: number | null
  numberOfDocumentsWithDueDateFromEquipmentTemplate: number | null
  permissionValue: IBoat['permissionValue']
  isBoatInWorkshop: boolean
  currentAccessGroup: string[]
  assetType: number
}

export interface IBoatListResult {
  client: IClient
  adminBoats: IAccessGroupBoat
  accessGroupBoats: IAccessGroupBoat[]
  singleBoats: IAccessGroupBoat
  crew: IAccessGroupBoat
}

export interface IUserEmailCategory {
  id: EntityId
  name: string
  enabledCount: number

  emails: IUserEmail[]
}

export interface IUserEmail {
  id: EntityId
  name: string
  enabled: boolean
}

export interface IUserSession {
  id: number
  userId: number
  refreshToken: string

  deviceInfo: {
    browser: string
    os: string
    device: string
  }
}

export interface IBoatDocument {
  boatId: number
  categoryName: string
  deleted: boolean
  documentCategory: { documentCategoryId: number; title: string }
  documentCategoryId: number
  documentFromEquipmentTemplate: boolean
  documentId: number
  dueDate: string
  dueDateNotificationDays: number
  dueDateExpired: boolean
  equipment: any
  equipmentId?: number
  file: IFile
  fileForSaving?: any
  fileId: number
  mainCategory0: null | MainCategory
  mainCategory0Id: number
  mainCategory1: null | MainCategory
  mainCategory1Id: number
  mainCategory2: null | MainCategory
  mainCategory2Id: number
  mainCategory3: null | MainCategory
  mainCategory3Id: number
  revisionId: string | number | any
  oldRevisions: any[]
  parentDocumentTemplatesDocumentLink: null
  registeredByUserId: number
  registeredDate: string
  sfi?: string
  sfiId?: number
  showInDashboard: boolean
  templateDocumentAdded: boolean
  templateDocumentDeleted: boolean
  title: string
  userId?: number
}

export interface IWorkshopCheckList {
  id: number
  name: string
  workshopBoatId: number
  checklistCompletionId: number
  checklistCompletion: CheckListCompletion
}

export interface IChecklist {
  id: number
  clientId: number
  name: string
  description: string
  type: ChecklistType
  createdAt: string
  deletedAt?: string
  workshopId: number
}

export interface IFileFolder {
  id: number
  name: string
  parentFolderId?: number
  parentFolder?: IFileFolder
  numOfDocuments: number
  numOfFolders: number
}

export interface IExerciseOccurence {
  notes: string
  id: number
  boatExerciseId: number
  boatExercise: IBoatExercise
  checklistCompletionId: number
  completed: boolean
  startDate: string
  endDate: string
  files: IFile[]
  occurenceCrew: IExerciseOccurenceCrew[]
}

export interface IExerciseOccurenceCrew {
  id: number
  userId: number
  exerciseOccurenceId: number
  user?: IUser
  exerciseOccurence?: IExerciseOccurence
}

export interface IExercise {
  id: number
  name: string
  note: string
  link: string
}

export interface IBoatExercise {
  id: number
  name?: string
  note?: string
  startDate?: any
  endDate?: any
  isReccuring?: boolean
  rRulePattern: any
  exercise?: any
  exerciseId?: number
}

export interface IGetDbOccurenceResponse {
  checklistCompletion: any
  crew: IUser[]
  occurence: IExerciseOccurence
  files: IFile[]
}

export interface IOccurenceOverview {
  comingUp: number
  overdue: number
  today: number
  firstExercise: any
}
export interface IAudit extends IEntity {
  userId?: string | number
  type?: string
  tableName?: string
  dateTime: any
  keyValues: string
  oldValues: string
  newValues: string
  affectedColumns: string
  primaryKey: string
  user: IUser
}

export interface SelectedDate {
  year: number
  month: number
}

export interface IWorkshopWorkOrderStatus {
  workOrderCount: number
  overDueWorkOrderCount: number
}

export interface IWorkshopWorkOrders {
  id: number
  boatId: number
  boatName: string
  title: string
  description: string
  executionDate: Date
  isWorkOrderBoatInDifferentWorkhop: boolean
}

export interface IWorkshopBoatWorkOrder {
  workshopId: number
  workshop: string
  workOrder: string
  status: string
}
export interface IBoatListOptions {
  groupByType: boolean
  search?: string
}

export interface IFeatures {
  id: number
  name: string
  description: string
  isActive: boolean
  isGlobal: boolean
}

export interface IClientFeatureMapping {
  id: number
  featureId: number
  name: string
  description: string
  isActive: boolean
  isGlobal: boolean
  startDate: Date
  endDate: Date
  startDateAsString: Date
  endDateAsString: Date
}

export interface ICertificateType {
  id: number
  name: string
  description: string
  templateVersionId: number
}

export interface ICertificateTemplateVersions {
  templateId: number
  templateVersionId: number
  name: string
  description: string
  version: number
  status: number
}

export interface ICertificateDataHistory {
  id: number
  certificateType: string
  certificateTemplateVersion: number
  isCompleted: boolean
}
export interface IProductCategory {
  productCategoryId: number
  name: string
}
export interface IProduct {
  id: number
  name: string
}
export interface ILubricantMap {
  id: number
  mainLubricantId: number
  mainLubricantName: string
  mainLubricantCategoryId: number
  alternativeLubricants: IAlternativeLubricants[]
  image: any
  isFromTemplate: boolean
}

export interface IAlternativeLubricants {
  id: number
  productId: number
  name: string
}

export interface IBoatStock {
  id: number
  info: IStockInfo
  clientId: number
}

export interface IStockInfo {
  totalProducts: number
  lowStockProducts: number
  outOfStockProducts: number
  totalEquipment: number
}

export interface BoatCardInfo {
  boatId: number
  numberOfDeviations: number
  highestDeviationSeverity: number | null
  closestTimeForDocumentExpire: string | null
  numberOfDocumentsWithDueDate: number
  numberOfDocumentsWithDueDateFromEquipmentTemplate: number
  numberOfMaintancesToday: number
  numberOfDocumentsCloseToExpire: number
  clientId: number
  clientName: string
  assetType: number
  name: string
}

export interface ClientAndBoatDeadlines {
  clientId: number
  clientName: string
  totalNumberOfDeviations: number
  totalHighestDeviationSeverity: number
  totalNumberOfDocumentsWithDueDate: number
  totalNumberOfDocumentsWithDueDateFromEquipmentTemplate: number
  totalNumberOfMaintenancesToday: number
  closestTimeForDocumentExpire: string | null
  boatsCardInfo: Record<number, BoatCardInfo>
  numberOfAssets: number
}
export interface WorkshopUserNotificationPreference {
  id: number
  notificationOnIncomingWo: boolean
}
export interface IWorkshopUserDetails {
  id: number
  email: string
  hasAnyOverDueWorkCases: boolean
  name: string
  startDate: Date
  endDate: Date
  userId: number
  workshopUserId: number
  roleId: number
  workcases: IWorkCasePreview[]
}

export interface IWorkshopWorkOrderSectionResponse {
  endDate?: Date
  startDate?: Date
  workOrder?: WorkshopCardContainerItem
  workshopId: number
  acceptedDate?: Date
  boat?: IBoatPreview
  description: string
  id: number
  status: WorkshopWorkOrderSection
  users?: IWorkshopUser[]
  workShopUserDetailsDto?: IWorkshopUser
  title?: string
  workCaseAnalysis?: AnalysisFormDataResponse
  hasUsers?: boolean
  budget?: number
  timePlan?: number
}

export interface IWorkCasePreview {
  id: number
  title: string
  description: string
  executionDate: string
  boat: IBoatPreview
  registeredByUser: string
  registeredByDate: Date
  startDate: Date
  endDate: Date
  status: WorkshopBoatStatus
}

export interface IPaginatedResponse {
  hasNextPage: boolean
  nextCursor: number
  totalCount?: number
}

export interface IWorkCasesPaginatedResponse extends IPaginatedResponse {
  data: IWorkshopWorkOrderSectionResponse[]
}

export interface IWorkOrderPaginatedResponse extends IPaginatedResponse {
  data: IWorkOrderPreview[]
}

export interface IAssetOverview {
  assetId: number
  assetType: number
  name: string
  urlFriendlyName: string
  assetStatusId: number
  mainImageViewToken: string | null
  mainImageRemoteUrl: string | null
  mainImageName: string
  isAssetInWorkshop: boolean
  owner: string
  year: number
  fuel: string
  model: string
  loh: number
  loa: number
  chassisNumber: string
  odometer: string
  addressLine: string
  size: string
  content: string
  mileage: number
  color: string
  mmsi: string
  registrationNumber: string
  bodyType: string
  brand: string
  vesselType: string
  boatTypeCategory: string
  currentAccessGroup: string[]
  propertyType: string
  serialNumber: string
}

export interface IVehiclePublicApiData {
  chassisNumber: string
  brand: string
  model: string
  color: string
  year: number
  firstTimeRegistered: Date
  fuel: string
  bodyType: string
  electricRange: number
  feeCategory: string
  wheelDrive: string
  maximumTrailerWeight: number
  usageCategory: string
}
export interface WorkOrderItem {
  rowId: number
  id?: number
  title?: string
  description?: string
  item?: any
}

export interface FollowUpItem {
  id: number
  hours?: number
  notes?: string
  status?: string
  files?: any
}

export type IWorkOrderItemSelectionProps =
  | {
      id?: undefined | null
      defaultValue?: undefined | null
      assetId: number
      onSubmit: (data: any) => void
      pop: () => void
      onEdit?: never
      selectedEquipmentIds?: number[]
      selectedDeviationIds?: number[]
      selectedMaintenanceIds?: number[]
    }
  | {
      id: number
      defaultValue: number
      assetId: number
      pop: () => void
      onSubmit?: never
      onEdit: (data: any) => void
      selectedEquipmentIds?: number[]
      selectedDeviationIds?: number[]
      selectedMaintenanceIds?: number[]
    }

export type IWorkOrderItemSelectionWithTitleProps =
  | {
      id?: undefined | null
      defaultTitle?: undefined | null
      defaultSelectedId?: undefined | null
      assetId: number
      onSubmit: (data: any) => void
      pop: () => void
      onEdit?: never
      selectedChecklistIds?: number[]
    }
  | {
      id: number
      defaultTitle: string
      defaultSelectedId: number
      assetId: number
      pop: () => void
      onSubmit?: never
      onEdit: (data: any) => void
      selectedChecklistIds?: number[]
    }

export interface IWorkcaseFollowup {
  id: number
  timePlan: number
  notes: string
  type: string
  fileName: any
  createdById?: number
  createdBy: string
  createdDate: Date
}

export interface IWorkorderV2 {
  id: number
  title: string
  description: string
  executionDate: Date
  assignedPersonId: number | null
  assignedPersonString: string
  workType: number
  priority: number
  documentItems: any
  equipmentItems: any
  deviationItems: any
  maintenanceItems: any
  checklistItems: any
  additionalChecklists: any
  workshopId: number | null
  files: any[]
  timePlan: number
  budget: number
  localityId?: number | null
  hasWorkshopWorkcase: boolean
}

export interface IAssetSummary {
  id: number
  name: string
  type: number
}

export interface IDashboardMaintenanceDto {
  assetId: number
  assetName: string
  assetType: number
  equipmentName: string | null
  imageViewToken: string | null
  interval: number | null
  lastDoneDate: string | null
  maintenanceId: number | null
  maintenanceTitle: string | null
  nextDueDate: string | null
  numberOfDoneMaintenance: number
  numberOfDoneMaintenanceToday: number | null
  numberOfMaintenanceToday: number
  numberOfOverdueMaintenances: number
  numberOfPastDueMaintenances: number | null
  repeatDay: string | null
  repeatMonth: string | null
  repeatStart: string | null
  repeatStop: string | null
  repeatType: string
  repeatWeek: string | null
  repeatWeekdays: number[] | null
  repeatWeekdaysRaw: string | null
  repeatYear: string | null
}

export interface IMainCategoryNode {
  id: string
  code: string
  level: number
  parentId?: string
  descriptionNo: string
  descriptionEn: string
  localeEntry?: string
  subCategories?: IMainCategoryNode[]
  expanded?: boolean
}

export interface TreeNodeProps {
  node: IMainCategoryNode
  level: number
  isSelected: boolean
  selectedCategory: (node: IMainCategoryNode) => void
  handleCategoryToggle: (nodeId: string) => void
  handleAddMaincategory?: (parentNode: IMainCategoryNode, newNode: IMainCategoryNode) => void
  handleDeleteMainCategory?: (nodeId: string) => void
  handleUpdateMainCategoryLocale?: (nodeId: string, updates: Partial<IMainCategoryNode>) => void
  maxLevels: number
  editingNodeId: string | null
  setEditingNodeId: (id: string | null) => void
}

export interface IAssetTypeFilterOptions {
  groupByType: any
  listMode: 'card' | 'list'
  search?: string
  selectedFilters: number[]
}
